import React, { useEffect, useState, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from '../../../../config/axios'
import styled from 'styled-components'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import ConfirmationModal from '../../../../components/ModalConfirmation'
import {
  Container,
  HeaderDiv,
  HeaderText,
  CloseDiv,
  ContentContainer,
  Section,
  SectionHeader,
  DetailGrid,
  DetailItem,
  DetailLabel,
  DetailValue,
  ButtonContainer,
  EditButton,
  DeleteButton,
} from './style'
import { BlurBackground } from '../style'
import cross from '../../../../assets/addStockModal/cross.svg'
import editIcon from '../../../../assets/edit-pen-white.svg'
import trash from '../../../../assets/equipment/Tables/trash.svg'
import { getTagTextColor } from '../../../../utils/colorContrast'
import OrdenoLoading from '../../../../components/OrdenoLoading'

const API_BASE_URL = `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-product`

function sanitizeCode(rawCode: any): string {
  if (rawCode == null) return ''
  const str = String(rawCode).trim()
  if (!str) return ''
  return str.replace(/\s*-\s*/g, '-')
}

const FlatTable = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  margin-top: 12px;
  width: 100%;
`

const FlatTableHead = styled.div<{ $columns: string }>`
  display: grid;
  grid-template-columns: ${props => props.$columns};
  background-color: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  padding: 10px 16px;
  
  span {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    color: #4b5563;
    letter-spacing: 0.05em;
  }
`

const FlatGridRow = styled.div<{ $columns: string }>`
  display: grid;
  grid-template-columns: ${props => props.$columns};
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #f3f4f6;

  &:last-child {
    border-bottom: none;
  }

  span {
    font-size: 13px;
    color: #111827;
  }
`

const ColorSwatchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const ColorSwatchCircle = styled.span<{ $hex?: string }>`
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${props => props.$hex ? (props.$hex.startsWith('#') ? props.$hex : `#${props.$hex}`) : '#e5e7eb'};
  border: 1px solid rgba(0,0,0,0.15);
  flex-shrink: 0;
`

interface ViewCollectionModalProps {
  collectionId: number
  onClose: () => void
}

interface CollectionDetailItem {
  label: string
  value: string | React.ReactNode
}

interface FabricItem {
  id: number
  stockLabel: string
  materialName: string
  colorName: string
  colorHex?: string
  code: string
  finishing: string
}

interface TrimItem {
  id: number
  resourceLabel: string
  typeLabel: string
  colorName: string
  colorHex?: string
  code: string
  finishing: string
}

interface FullCollectionData {
  general: CollectionDetailItem[]
  families: CollectionDetailItem[]
  grades: CollectionDetailItem[]
  groups: CollectionDetailItem[]
  episodes: CollectionDetailItem[]
  fabrics: FabricItem[]
  trims: TrimItem[]
  colors: CollectionDetailItem[]
}

const ViewCollectionModal: React.FC<ViewCollectionModalProps> = ({
  collectionId,
  onClose,
}) => {
  const navigate = useNavigate()
  const { setFeedback } = useContext(FeedbackContext)

  const [collectionData, setCollectionData] =
    useState<FullCollectionData | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false)

  useEffect(() => {
    const fetchCollectionData = async () => {
      if (!collectionId) {
        setLoading(false)
        return
      }

      setLoading(true)
      setError(null)

      try {
        const token = localStorage.getItem('token')
        const response = await axios.get(
          `${API_BASE_URL}/collections/${collectionId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        )

        const data = response.data

        const mappedGeneral: CollectionDetailItem[] = [
          { label: 'ID', value: data.id?.toString() || 'N/A' },
          { label: 'Nome da Coleção', value: data.name || 'N/A' },
          { label: 'Marca', value: data.brand?.trim() || 'N/A' },
        ]

        let mappedFamilies: CollectionDetailItem[] = []
        try {
          const familiesResponse = await axios.get(
            `${API_BASE_URL}/collections/${collectionId}/families`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          )
          const familiesData = familiesResponse.data
          const familyNames = Array.isArray(familiesData) && familiesData.length > 0
            ? familiesData.map((f: any) => f.name).join(', ')
            : 'Nenhuma família cadastrada'
          mappedFamilies = [{ label: 'Famílias', value: familyNames }]
        } catch (err) {
          mappedFamilies = [{ label: 'Famílias', value: 'Nenhuma família encontrada' }]
        }

        const gradesByType: Record<string, Set<string>> = {}
        ;(data.GradeInCollection || []).forEach((g: any) => {
          const typeName = g.Grade?.GraduationType?.name || g.Grade?.type?.name || 'Sem tipo'
          const gradeName = g.Grade?.name
          if (!gradeName) return
          if (!gradesByType[typeName]) gradesByType[typeName] = new Set()
          gradesByType[typeName].add(gradeName)
        })

        const gradeLines = Object.entries(gradesByType).map(([type, grades]) => {
          const arr = Array.from(grades)
          return (
            <span key={type}>
              <span style={{ fontWeight: 600 }}>{type}:</span>
              <span style={{ fontWeight: 400 }}> {arr.join(', ')}</span>
            </span>
          )
        })

        const mappedGrades: CollectionDetailItem[] = [
          {
            label: 'Grades',
            value:
              gradeLines.length > 0 ? (
                <span>
                  {gradeLines.reduce<React.ReactNode[]>((acc, line, idx) => {
                    if (idx > 0) acc.push(' | ')
                    acc.push(line)
                    return acc
                  }, [])}
                </span>
              ) : (
                'Nenhuma grade'
              ),
          },
        ]

        const groupNames = (data.GroupInCollection || [])
          .map((g: any) => g.Group?.name)
          .filter(Boolean)
        const mappedGroups: CollectionDetailItem[] = [
          { label: 'Grupos', value: groupNames.length > 0 ? groupNames.join(', ') : 'Nenhum grupo' },
        ]

        const getStockFinishing = (stock: any): string =>
          String(stock?.Finishing?.type ?? stock?.finishing ?? '').trim()

        const buildStockLabel = (stock: any): string => {
          if (!stock) return ''
          const materialName = (stock?.Material?.name
            ? String(stock.Material.name)
            : stock?.material
              ? String(stock.material)
              : '').trim()
          const description = (stock?.description ? String(stock.description) : '').trim()
          const rawCode = (stock?.Code?.code != null && String(stock.Code.code).trim() !== ''
            ? String(stock.Code.code)
            : stock?.code != null && String(stock.code).trim() !== ''
              ? String(stock.code)
              : stock?.codeId
                ? String(stock.codeId)
                : '').trim()
          const codeStr = sanitizeCode(rawCode)
          const parts: string[] = []
          if (codeStr) parts.push(codeStr)
          if (materialName) parts.push(materialName)
          if (description && description.toLowerCase() !== materialName.toLowerCase()) {
            parts.push(description)
          }
          return parts.filter(Boolean).join(' - ') || `Stock ${stock?.id ?? ''}`
        }

        const buildResourceLabel = (resource: any): string => {
          if (!resource) return ''
          const name = (resource?.name ? String(resource.name) : '').trim()
          const description = (resource?.description ? String(resource.description) : '').trim()
          const rawCode = (resource?.Code?.code != null && String(resource.Code.code).trim() !== ''
            ? String(resource.Code.code)
            : resource?.code != null && String(resource.code).trim() !== ''
              ? String(resource.code)
              : resource?.codeId
                ? String(resource.codeId)
                : '').trim()
          const codeStr = sanitizeCode(rawCode)
          const parts: string[] = []
          if (codeStr) parts.push(codeStr)
          if (name) parts.push(name)
          if (description && description.toLowerCase() !== name.toLowerCase()) {
            parts.push(description)
          }
          return parts.filter(Boolean).join(' - ') || `Resource ${resource?.id ?? ''}`
        }

        const fabricRows: FabricItem[] = []
        const trimRows: TrimItem[] = []

        ;(data.CollectionMaterial || []).forEach((cm: any) => {
          const kind = cm.kind === 'TRIM' ? 'TRIM' : 'FABRIC'
          if (kind === 'TRIM') {
            const tr = cm.TrimResource
            const ic = tr?.ItemColor
            const rawCode =
              tr?.Code?.code != null && String(tr.Code.code).trim() !== ''
                ? String(tr.Code.code)
                : tr?.code != null && String(tr.code).trim() !== ''
                  ? String(tr.code)
                  : tr?.codeId != null
                    ? String(tr.codeId)
                    : ''
            const codeStr = sanitizeCode(rawCode)
            trimRows.push({
              id: cm.id,
              resourceLabel: tr?.name ? buildResourceLabel(tr) : '',
              typeLabel: tr?.Type_Category_Resource?.name
                ? String(tr.Type_Category_Resource.name)
                : tr?.Category_Resource?.name
                  ? String(tr.Category_Resource.name)
                  : '—',
              colorName: ic?.name ? String(ic.name) : '—',
              colorHex: ic?.hex ? String(ic.hex) : (ic?.Pantone?.hex ? String(ic.Pantone.hex) : undefined),
              code: codeStr,
              finishing: tr?.Finishing?.type ? String(tr.Finishing.type) : (tr?.finishing ? String(tr.finishing) : '—'),
            })
          } else {
            const stock = cm.Stock
            const itemColor = stock?.ItemColor
            const rawCode =
              stock?.Code?.code != null && String(stock.Code.code).trim() !== ''
                ? String(stock.Code.code)
                : stock?.code != null && String(stock.code).trim() !== ''
                  ? String(stock.code)
                  : stock?.codeId != null
                    ? String(stock.codeId)
                    : ''
            const codeStr = sanitizeCode(rawCode)
            const materialName = cm.Material?.name || stock?.Material?.name || '—'
            fabricRows.push({
              id: cm.id,
              stockLabel: stock ? buildStockLabel(stock) : '',
              materialName,
              colorName: itemColor?.name ? String(itemColor.name) : '—',
              colorHex: itemColor?.hex ? String(itemColor.hex) : (itemColor?.Pantone?.hex ? String(itemColor.Pantone.hex) : undefined),
              code: codeStr,
              finishing: getStockFinishing(stock),
            })
          }
        })

        const mappedColors: CollectionDetailItem[] = (data.CollectionColor || []).map((cc: any) => {
          const pantone = cc.Pantone || {}
          const hex = pantone.hex || cc.hex
          return {
            label: cc.name || 'Cor sem nome',
            value: (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {hex && (
                  <span
                    style={{
                      display: 'inline-block',
                      width: 12,
                      height: 12,
                      borderRadius: 3,
                      backgroundColor: hex.startsWith('#') ? hex : `#${hex}`,
                      border: '1px solid rgba(0,0,0,0.15)',
                    }}
                    title={hex}
                  />
                )}
                <span style={{ fontWeight: 400 }}>
                  {pantone.code ? `Pantone: ${pantone.code}` : 'Sem Pantone'}
                </span>
              </div>
            )
          }
        })

        const episodeNames = (data.Episode || [])
          .map((ep: any) => ep.name)
          .filter(Boolean)
        const mappedEpisodes: CollectionDetailItem[] = [
          { label: 'Episódios', value: episodeNames.length > 0 ? [...new Set(episodeNames)].join(', ') : 'Nenhum episódio' },
        ]

        setCollectionData({
          general: mappedGeneral,
          families: mappedFamilies,
          grades: mappedGrades,
          groups: mappedGroups,
          episodes: mappedEpisodes,
          fabrics: fabricRows,
          trims: trimRows,
          colors: mappedColors,
        })
        setLoading(false)
      } catch (err: any) {
        console.error('Erro ao buscar detalhes da coleção:', err)
        setError(
          err.response?.data?.message ||
            'Erro ao carregar dados da coleção. Tente novamente.',
        )
        setLoading(false)
      }
    }

    fetchCollectionData()
  }, [collectionId])

  const handleClose = () => {
    onClose()
  }

  const handleEditCollection = () => {
    navigate(`/panel/collection/edit/${collectionId}`)
    onClose()
  }

  const handleDeleteCollection = async () => {
    setShowDeleteConfirm(true)
  }

  const handleConfirmDeleteCollection = async () => {

    const token = localStorage.getItem('token')
    if (!token) {
      setFeedback({ isOpen: true, statusOk: false, message: 'Sessão expirada. Faça login novamente.' })
      setShowDeleteConfirm(false)
      return
    }

    try {
      await axios.delete(`${API_BASE_URL}/collections/${collectionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      setFeedback({ isOpen: true, statusOk: true, message: 'Coleção excluída com sucesso!' })
      setShowDeleteConfirm(false)
      onClose()
      window.location.reload()
    } catch (err: any) {
      console.error('Erro ao excluir coleção:', err)
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: err.response?.data?.message || 'Erro ao excluir coleção. Tente novamente.',
      })
    }
  }

  const handleCancelDeleteCollection = () => {
    setShowDeleteConfirm(false)
  }

  if (loading) {
    return (
      <BlurBackground>
        <Container>
          <HeaderDiv>
            <HeaderText>Carregando dados da coleção...</HeaderText>
            <CloseDiv onClick={handleClose}>
              <img src={cross} alt='Fechar' />
            </CloseDiv>
          </HeaderDiv>
          <ContentContainer>
            <OrdenoLoading variant='inline' text='Carregando informações da coleção...' style={{ padding: '3rem 0' }} />
          </ContentContainer>
        </Container>
      </BlurBackground>
    )
  }

  if (error) {
    return (
      <BlurBackground>
        <Container>
          <HeaderDiv>
            <HeaderText>Erro ao carregar dados</HeaderText>
            <CloseDiv onClick={handleClose}>
              <img src={cross} alt='Fechar' />
            </CloseDiv>
          </HeaderDiv>
          <ContentContainer>
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <p style={{ color: 'red', marginBottom: '1rem' }}>{error}</p>
              <button onClick={handleClose}>Fechar</button>
            </div>
          </ContentContainer>
        </Container>
      </BlurBackground>
    )
  }

  if (!collectionData) {
    return (
      <BlurBackground>
        <Container>
          <HeaderDiv>
            <HeaderText>Coleção não encontrada</HeaderText>
            <CloseDiv onClick={handleClose}>
              <img src={cross} alt='Fechar' />
            </CloseDiv>
          </HeaderDiv>
          <ContentContainer>
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              Nenhum dado encontrado para a coleção com ID: {collectionId}.
            </div>
          </ContentContainer>
        </Container>
      </BlurBackground>
    )
  }

  const collectionNameItem = collectionData.general.find(
    (item) => item.label === 'Nome da Coleção',
  )
  const displayName = collectionNameItem
    ? collectionNameItem.value
    : `Coleção #${collectionId}`

  return (
    <>
    <BlurBackground>
      <Container>
        <HeaderDiv>
          <HeaderText>Coleção: {displayName}</HeaderText>
          <CloseDiv onClick={handleClose}>
            <img src={cross} alt='Fechar' />
          </CloseDiv>
        </HeaderDiv>

        <ContentContainer>
          <Section>
            <SectionHeader>Dados gerais</SectionHeader>
            <DetailGrid>
              {collectionData.general.map((item, index) => (
                <DetailItem key={index}>
                  <DetailLabel>{item.label}</DetailLabel>
                  <DetailValue>{item.value}</DetailValue>
                </DetailItem>
              ))}
            </DetailGrid>
          </Section>

          <Section>
            <SectionHeader>Grupos</SectionHeader>
            <DetailGrid>
              {collectionData.groups.map((item, index) => (
                <DetailItem key={index}>
                  <DetailLabel>{item.label}</DetailLabel>
                  <DetailValue>{item.value}</DetailValue>
                </DetailItem>
              ))}
            </DetailGrid>
          </Section>

          <Section>
            <SectionHeader>Famílias</SectionHeader>
            <DetailGrid>
              {collectionData.families.map((item, index) => (
                <DetailItem key={index}>
                  <DetailLabel>{item.label}</DetailLabel>
                  <DetailValue>{item.value}</DetailValue>
                </DetailItem>
              ))}
            </DetailGrid>
          </Section>

          <Section>
            <SectionHeader>Episódios</SectionHeader>
            <DetailGrid>
              {collectionData.episodes.map((item, index) => (
                <DetailItem key={index}>
                  <DetailLabel>{item.label}</DetailLabel>
                  <DetailValue>{item.value}</DetailValue>
                </DetailItem>
              ))}
            </DetailGrid>
          </Section>

          <Section>
            <SectionHeader>Grades</SectionHeader>
            <DetailGrid>
              {collectionData.grades.map((item, index) => (
                <DetailItem key={index}>
                  <DetailLabel>{item.label}</DetailLabel>
                  <DetailValue>{item.value}</DetailValue>
                </DetailItem>
              ))}
            </DetailGrid>
          </Section>

          {collectionData.colors && collectionData.colors.length > 0 && (
            <Section>
              <SectionHeader>Cores da Coleção</SectionHeader>
              <DetailGrid>
                {collectionData.colors.map((color, index) => (
                  <DetailItem key={index}>
                    <DetailLabel>{color.label}</DetailLabel>
                    <DetailValue>{color.value}</DetailValue>
                  </DetailItem>
                ))}
              </DetailGrid>
            </Section>
          )}



          <Section>
            <SectionHeader>Matérias-primas / Tecidos</SectionHeader>
            {collectionData.fabrics.length > 0 ? (
              <FlatTable>
                <FlatTableHead $columns='minmax(140px, 1fr) minmax(220px, 2fr) 130px 120px'>
                  <span>Cor</span>
                  <span>Matéria-prima</span>
                  <span>Acabamento</span>
                  <span>Código</span>
                </FlatTableHead>
                {collectionData.fabrics.map((row) => (
                  <FlatGridRow key={row.id} $columns='minmax(140px, 1fr) minmax(220px, 2fr) 130px 120px'>
                    <ColorSwatchContainer>
                      <ColorSwatchCircle $hex={row.colorHex} />
                      <span>{row.colorName}</span>
                    </ColorSwatchContainer>
                    <span>{row.stockLabel || row.materialName}</span>
                    <span>{row.finishing || '—'}</span>
                    <span>{row.code || '—'}</span>
                  </FlatGridRow>
                ))}
              </FlatTable>
            ) : (
              <div style={{ color: '#6b7280', fontSize: '13px' }}>Nenhuma matéria-prima cadastrada</div>
            )}
          </Section>

          <Section>
            <SectionHeader>Insumos / aviamentos</SectionHeader>
            {collectionData.trims.length > 0 ? (
              <FlatTable>
                <FlatTableHead $columns='minmax(140px, 1fr) 120px minmax(200px, 2fr) 120px 110px'>
                  <span>Cor</span>
                  <span>Tipo</span>
                  <span>Nome</span>
                  <span>Acabamento</span>
                  <span>Código</span>
                </FlatTableHead>
                {collectionData.trims.map((row) => (
                  <FlatGridRow key={row.id} $columns='minmax(140px, 1fr) 120px minmax(200px, 2fr) 120px 110px'>
                    <ColorSwatchContainer>
                      <ColorSwatchCircle $hex={row.colorHex} />
                      <span>{row.colorName}</span>
                    </ColorSwatchContainer>
                    <span>{row.typeLabel || '—'}</span>
                    <span>{row.resourceLabel || '—'}</span>
                    <span>{row.finishing || '—'}</span>
                    <span>{row.code || '—'}</span>
                  </FlatGridRow>
                ))}
              </FlatTable>
            ) : (
              <div style={{ color: '#6b7280', fontSize: '13px' }}>Nenhum insumo / aviamento cadastrado</div>
            )}
          </Section>

        </ContentContainer>

        <ButtonContainer>
          <DeleteButton onClick={handleDeleteCollection}>
            <img src={trash} alt='Excluir' />
            Excluir coleção
          </DeleteButton>
          <EditButton style={{ marginLeft: '1rem' }} onClick={handleEditCollection}>
            <img src={editIcon} alt='Editar' />
            Editar coleção
          </EditButton>
        </ButtonContainer>
      </Container>
    </BlurBackground>
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        title='Confirmar exclusão'
        message='Tem certeza que deseja excluir esta coleção? Esta ação não pode ser desfeita.'
        actions={[
          { label: 'Cancelar', onClick: handleCancelDeleteCollection, variant: 'outline' },
          { label: 'Excluir', onClick: handleConfirmDeleteCollection, variant: 'danger' },
        ]}
      />
    </>
  )
}

export default ViewCollectionModal